import { HttpClient } from '@angular/common/http';
import {
    EntityActionFactory,
    EntityCacheDispatcher, EntityOp,
    HttpUrlGenerator, Logger, MergeStrategy, QueryParams
} from '@ngrx/data';
import { Observable, Subscription } from 'rxjs/index';
import { map } from 'rxjs/operators';
import { WebsocketBrokerService } from '../../../core/services/websocket-broker.service';
import { WebSocketEntityDataServiceBase } from '../../../state/entity/web-socket-entity-data-service-base';
import { Appointment } from '../../appointments/state/appointment.model';
import { patientAppointmentEntityMetadata } from './patient-appointment.entity';

export const entityActionFactory = new EntityActionFactory();

/**
 * This is the base data service to get appointment list (called by NgRx Effects to make actuall calls to the backend).
 * This must be specialized e.g. to get AppointmentList entity data.
 */
export abstract class PatientAppointmentEntityDataServiceBase<T> extends WebSocketEntityDataServiceBase<Appointment> {
    subscriptions: Subscription = new Subscription();

    constructor(http: HttpClient,
                httpUrlGenerator: HttpUrlGenerator,
                logger: Logger,
                public entityCacheDispatcher: EntityCacheDispatcher,
                public websocketBrokerService: WebsocketBrokerService,
                entityName: string,
                public webSocketTopic: string) {
        super(entityName, http, httpUrlGenerator);
    }

    getWithQuery(parameters: string | QueryParams): Observable<Array<Appointment>> {
        return super.getWithQuery(parameters) || this.getAppointments(parameters);
    }

    abstract getAppointments(parameters: string | QueryParams): Observable<Array<Appointment>>;

    subscribeWebSocketJobWithBody(initialBody: any): void {
        // NOTE: Since we're not subscribing to the result inside an NgRx Effect we need to subscribe here and manage the
        //       subscription.
       const websocketBrokerServiceSubscription =
            this.websocketBrokerService.getRemoteObservable(this.webSocketTopic, initialBody)
                .pipe(
                    map(pushedPayload => {
                        if (pushedPayload.error !== undefined) {
                            // TODO: change to modal window
                            alert(pushedPayload.error);
                            const data: any = [];
                            data.refreshing = false;
                            const entityAction = entityActionFactory.create<Array<Appointment>>(
                                patientAppointmentEntityMetadata.entityName,
                                EntityOp.QUERY_MANY_SUCCESS,
                                data,
                                {
                                    tag: 'PatientAppointmentEffects',
                                    mergeStrategy: MergeStrategy.OverwriteChanges
                                }
                            );
                            this.entityCacheDispatcher.dispatch(entityAction);
                        } else {
                            const data: any = [];
                            if (pushedPayload.queryFilter.personId !== +initialBody.parameters.personId) {
                                data.queryFilter = initialBody.parameters;
                            } else {
                                data.queryFilter = pushedPayload.queryFilter;
                                // For some reason the array from the backend is not useful for adding properties
                                // so we create a new adding the messages
                                if (pushedPayload.appointments !== undefined) {
                                    pushedPayload.appointments.forEach(appointment => {
                                        data.push(appointment);
                                    });
                                }
                            }

                            const entityAction = entityActionFactory.create<Array<Appointment>>(
                                patientAppointmentEntityMetadata.entityName,
                                EntityOp.QUERY_MANY_SUCCESS,
                                data,
                                {
                                    tag: 'PatientAppointmentEffects',
                                    mergeStrategy: MergeStrategy.OverwriteChanges
                                }
                            );
                            this.entityCacheDispatcher.dispatch(entityAction);
                        }
                    })
                )
                .subscribe();

       this.subscriptions.add(websocketBrokerServiceSubscription);
    }

    unsubscribeWebSocketJob(): void {
        this.websocketBrokerService.unsubscribe(this.webSocketTopic);
        this.subscriptions.unsubscribe();
        this.subscriptions = new Subscription();
    }

    refreshWebSocketJobWithBody(messageBody: any): void {
        this.websocketBrokerService.sendAction('refresh', this.webSocketTopic, messageBody);
    }
}
